import { useState, useEffect } from 'react';
import FileUpload from './components/FileUpload';
import ProcessingStatus from './components/ProcessingStatus';
import ResultsTable from './components/ResultsTable';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import { Profile } from './components/Profile';
import {
  processWorkflow,
  WorkflowResult,
  WorkflowProgressEvent,
  getCurrentUser,
  logout,
  verifyToken,
  getEbayOAuthUrl,
  disconnectEbay,
  User
} from './api/client';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [results, setResults] = useState<WorkflowResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentBatch, setCurrentBatch] = useState<number | undefined>(undefined);
  const [totalBatches, setTotalBatches] = useState<number | undefined>(undefined);
  const [partialResults, setPartialResults] = useState<WorkflowResult['results']>([]);
  const [partialSummary, setPartialSummary] = useState<WorkflowResult['summary']>({
    totalUPCs: 0,
    qualifiedSellers: 0,
    messagesGenerated: 0,
    unqualifiedSellers: 0,
    noResults: 0,
    errors: 0,
  });
  const [ebayConnecting, setEbayConnecting] = useState(false);
  const [ebayDisconnecting, setEbayDisconnecting] = useState(false);

  // Helper function to check if eBay token is expired
  const isEbayTokenExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return true;
    const expirationDate = new Date(expiresAt);
    return expirationDate.getTime() < new Date().getTime();
  };

  const isEbayConnected = currentUser?.ebayAccessTokenExpiresAt && !isEbayTokenExpired(currentUser.ebayAccessTokenExpiresAt);

  const handleEbayConnect = async () => {
    try {
      setEbayConnecting(true);
      setError(null);
      console.log('Initiating eBay OAuth...');
      const { authUrl } = await getEbayOAuthUrl();
      console.log('Received OAuth URL:', authUrl);
      window.location.href = authUrl;
    } catch (err: any) {
      console.error('Failed to get eBay OAuth URL:', err);
      setError(err.message || 'Failed to initiate eBay OAuth. Please try again.');
      setEbayConnecting(false);
    }
  };

  const handleEbayDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your eBay account? You will need to reconnect to use the workflow.')) {
      return;
    }

    try {
      setEbayDisconnecting(true);
      setError(null);
      await disconnectEbay();

      // Refresh user data to reflect disconnection
      const user = await getCurrentUser();
      setCurrentUser(user);

      console.log('eBay account disconnected successfully');
    } catch (err: any) {
      console.error('Failed to disconnect eBay account:', err);
      setError(err.message || 'Failed to disconnect eBay account. Please try again.');
    } finally {
      setEbayDisconnecting(false);
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const isValid = await verifyToken();
      if (isValid) {
        try {
          const user = await getCurrentUser();
          setCurrentUser(user);
          setAuthenticated(true);
        } catch {
          setAuthenticated(false);
        }
      } else {
        setAuthenticated(false);
      }
      setAuthLoading(false);
    };

    checkAuth();

    // Handle eBay OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const ebayAuthSuccess = urlParams.get('ebayAuthSuccess');
    const ebayAuthError = urlParams.get('ebayAuthError');

    if (ebayAuthSuccess) {
      console.log('eBay OAuth successful!');
      // Re-authenticate user to get updated user data with eBay tokens
      handleAuthSuccess();
      // Clear query params
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (ebayAuthError) {
      setError(decodeURIComponent(ebayAuthError));
      // Clear query params
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Listen for unauthorized events
    const handleUnauthorized = () => {
      setAuthenticated(false);
      setCurrentUser(null);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const handleAuthSuccess = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      setAuthenticated(true);
    } catch (err) {
      console.error('Failed to get current user:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setAuthenticated(false);
      setCurrentUser(null);
      setFile(null);
      setResults(null);
      setShowUserManagement(false);
      setShowProfile(false);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
  };

  const handleFileRemove = () => {
    setFile(null);
    setResults(null);
    setError(null);
  };

  const handleProcess = async () => {
    if (!file) return;

    setProcessing(true);
    setProgress(0);
    setStatus('Uploading file...');
    setError(null);
    setResults(null);
    setCurrentBatch(undefined);
    setTotalBatches(undefined);
    setPartialResults([]);
    setPartialSummary({
      totalUPCs: 0,
      qualifiedSellers: 0,
      messagesGenerated: 0,
      unqualifiedSellers: 0,
      noResults: 0,
      errors: 0,
    });

    try {
      setStatus('Processing UPCs...');

      const result = await processWorkflow(file, (event: WorkflowProgressEvent) => {
        // Handle streaming progress events
        switch (event.type) {
          case 'start':
            setTotalBatches(event.totalBatches);
            setStatus(`Starting processing of ${event.totalUPCs} UPCs in ${event.totalBatches} batches...`);
            setProgress(0);
            setPartialSummary(prev => ({ ...prev, totalUPCs: event.totalUPCs }));
            break;

          case 'batch_start':
            setCurrentBatch(event.batch);
            setTotalBatches(event.totalBatches);
            setStatus(`Processing batch ${event.batch}/${event.totalBatches}...`);
            setProgress((event.batch - 1) / event.totalBatches * 100);
            break;

          case 'batch_complete':
            setCurrentBatch(event.batch);
            setTotalBatches(event.totalBatches);
            setStatus(`Completed batch ${event.batch}/${event.totalBatches} - ${event.summary.qualifiedSellers} qualified sellers found`);
            setProgress(event.batch / event.totalBatches * 100);
            
            // Accumulate partial results for real-time display
            setPartialResults(prev => [...prev, ...event.results]);
            
            // Update partial summary with cumulative counts
            setPartialSummary(prev => ({
              ...prev,
              qualifiedSellers: prev.qualifiedSellers + event.summary.qualifiedSellers,
              messagesGenerated: prev.messagesGenerated + event.summary.messagesGenerated,
              unqualifiedSellers: prev.unqualifiedSellers + event.summary.unqualifiedSellers,
              noResults: prev.noResults + event.summary.noResults,
              errors: prev.errors + event.summary.errors,
            }));
            break;

          case 'complete':
            setProgress(100);
            setStatus('Complete!');
            setResults(event.finalResult);
            break;

          case 'error':
            const errorMsg = event.batch 
              ? `Error in batch ${event.batch}: ${event.error}`
              : `Error: ${event.error}`;
            setError(errorMsg);
            setStatus('');
            break;
        }
      });
      
      // Final result is set
      setProgress(100);
      setStatus('Complete!');
      setResults(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during processing');
      setStatus('');
    } finally {
      setProcessing(false);
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="container">
        <div className="auth-loading">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show authentication views if not authenticated
  if (!authenticated) {
    return <Login onSuccess={handleAuthSuccess} />;
  }

  // Show main application if authenticated
  return (
    <div>
      <div className="container">
        <div className="header">
          <div className="header-content">
            <div>
              <h1>🎯 eBay Seller Scout</h1>
              <p>AI-powered seller outreach automation for UPC products</p>
            </div>
            <div className="header-actions">
              {currentUser && (
                <>
                  {/* eBay Connection Status Indicator */}
                  <div className="ebay-status-indicator">
                    <div className={`status-dot ${isEbayConnected ? 'connected' : 'disconnected'}`}></div>
                    <span className="status-text">
                      {isEbayConnected ? 'eBay Connected' : 'eBay Disconnected'}
                    </span>
                    {isEbayConnected ? (
                      <button
                        className="ebay-disconnect-btn"
                        onClick={handleEbayDisconnect}
                        disabled={ebayDisconnecting}
                        title="Disconnect eBay account"
                      >
                        {ebayDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                      </button>
                    ) : (
                      <button
                        className="ebay-connect-btn"
                        onClick={handleEbayConnect}
                        disabled={ebayConnecting}
                      >
                        {ebayConnecting ? 'Connecting...' : 'Connect eBay'}
                      </button>
                    )}
                  </div>

                  <span className="user-info">
                    👤 {currentUser.email}
                    {currentUser.role === 'admin' && (
                      <span className="admin-badge">Admin</span>
                    )}
                  </span>
                  <button
                    className="profile-btn"
                    onClick={() => {
                      setShowProfile(!showProfile);
                      setShowUserManagement(false);
                    }}
                  >
                    {showProfile ? 'Back to App' : '⚙️ Profile'}
                  </button>
                  {currentUser.role === 'admin' && (
                    <button
                      className="user-mgmt-btn"
                      onClick={() => {
                        setShowUserManagement(!showUserManagement);
                        setShowProfile(false);
                      }}
                    >
                      {showUserManagement ? 'Back to App' : '👥 Manage Users'}
                    </button>
                  )}
                  <button className="logout-btn" onClick={handleLogout}>
                    🚪 Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {showProfile && currentUser ? (
          <Profile 
            userEmail={currentUser.email}
          />
        ) : showUserManagement && currentUser ? (
          <UserManagement currentUser={currentUser} />
        ) : (
          <>
            {!isEbayConnected && (
              <div className="warning-message">
                <strong>⚠️ eBay Connection Required</strong>
                <p>Please connect your eBay account to use the workflow. Click "Connect eBay" in the header above.</p>
              </div>
            )}

            <FileUpload
              file={file}
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              disabled={processing || !isEbayConnected}
            />

            {file && !processing && !results && (
              <button 
                className="process-btn" 
                onClick={handleProcess}
                disabled={!isEbayConnected}
              >
                {!isEbayConnected ? '🔒 Connect eBay to Start' : 'Start Processing'}
              </button>
            )}

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            {processing && (
              <ProcessingStatus 
                progress={progress} 
                status={status}
                currentBatch={currentBatch}
                totalBatches={totalBatches}
              />
            )}
          </>
        )}
      </div>

      {/* Show partial results during processing or final results when complete */}
      {(partialResults.length > 0 || results) && !showUserManagement && !showProfile && (
        <div className="container">
          <ResultsTable results={results || { results: partialResults, summary: partialSummary }} />
          {processing && partialResults.length > 0 && (
            <div style={{ marginTop: '10px', textAlign: 'center', color: '#666' }}>
              <em>Results updating in real-time as batches complete...</em>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;


